import { useCallback, useEffect, useRef, useState } from "react";
import { getPageActivity, subscribePageActivity } from "./pageActivity";

export interface Resource<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

export function useResource<T>(
  key: string,
  loader: (signal: AbortSignal) => Promise<T>,
  refreshMs?: number,
  coalesce = false,
): Resource<T> & { updateData: (updater: (data: T | null) => T | null) => void } {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [version, setVersion] = useState(0);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const mutationGeneration = useRef(0);
  const refreshRef = useRef<(() => void) | null>(null);
  const activityManaged = useRef(false);
  if (refreshMs) activityManaged.current = true;
  const reload = useCallback(() => {
    if (coalesce) refreshRef.current?.();
    else setVersion((value) => value + 1);
  }, [coalesce]);
  const updateData = useCallback((updater: (data: T | null) => T | null) => {
    mutationGeneration.current++;
    setData(updater);
  }, []);

  useEffect(() => {
    if (refreshMs || activityManaged.current) {
      let live = true;
      let active = false;
      let trailing = false;
      let timer = 0;
      let requestIdentity = 0;
      let controller: AbortController | undefined;
      const run = () => {
        const page = getPageActivity();
        if (!live || !page.active) return;
        if (active) {
          trailing = true;
          return;
        }
        active = true;
        const identity = ++requestIdentity;
        const activation = page.activation;
        controller = new AbortController();
        const signal = controller.signal;
        const generation = mutationGeneration.current;
        setLoading(true);
        void loaderRef
          .current(signal)
          .then(
            (value) => {
              const currentPage = getPageActivity();
              if (
                live &&
                identity === requestIdentity &&
                !signal.aborted &&
                currentPage.active &&
                currentPage.activation === activation &&
                generation === mutationGeneration.current
              ) {
                setData(value);
                setError(null);
              }
            },
            (reason: unknown) => {
              if (live && identity === requestIdentity && !signal.aborted) {
                setError(reason instanceof Error ? reason : new Error("Could not load data"));
              }
            },
          )
          .finally(() => {
            if (!live || identity !== requestIdentity) return;
            active = false;
            if (!getPageActivity().active) {
              setLoading(false);
            } else if (trailing) {
              trailing = false;
              run();
            } else {
              setLoading(false);
              if (refreshMs) timer = window.setTimeout(run, refreshMs);
            }
          });
      };
      const onActivity = () => {
        window.clearTimeout(timer);
        timer = 0;
        if (!getPageActivity().active) {
          trailing = false;
          requestIdentity += 1;
          active = false;
          controller?.abort();
          controller = undefined;
          setLoading(false);
          return;
        }
        run();
      };
      const unsubscribe = subscribePageActivity(onActivity);
      if (coalesce) refreshRef.current = run;
      run();
      return () => {
        live = false;
        requestIdentity += 1;
        window.clearTimeout(timer);
        controller?.abort();
        unsubscribe();
        if (coalesce) refreshRef.current = null;
      };
    }
    if (coalesce) {
      let live = true,
        active = false,
        trailing = false;
      let controller: AbortController | undefined;
      const run = () => {
        if (!live) return;
        if (active) {
          trailing = true;
          return;
        }
        active = true;
        controller = new AbortController();
        const signal = controller.signal;
        setLoading(true);
        void Promise.resolve()
          .then(() => {
            const generation = mutationGeneration.current;
            return loaderRef.current(signal).then((value) => ({ generation, value }));
          })
          .then(
            ({ generation, value }) => {
              if (live && generation === mutationGeneration.current) {
                setData(value);
                setError(null);
              }
            },
            (reason: unknown) => {
              trailing = false;
              if (live)
                setError(reason instanceof Error ? reason : new Error("Could not load data"));
            },
          )
          .finally(() => {
            active = false;
            if (!live) return;
            if (trailing) {
              trailing = false;
              run();
            } else setLoading(false);
          });
      };
      refreshRef.current = run;
      run();
      return () => {
        live = false;
        refreshRef.current = null;
        controller?.abort();
      };
    }
    const controller = new AbortController();
    setLoading(true);
    const generation = mutationGeneration.current;
    loaderRef.current(controller.signal).then(
      (value) => {
        if (!controller.signal.aborted && generation === mutationGeneration.current) {
          setData(value);
          setError(null);
        }
        if (!controller.signal.aborted) setLoading(false);
      },
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason : new Error("Could not load data"));
          setLoading(false);
        }
      },
    );
    return () => {
      controller.abort();
    };
  }, [key, version, coalesce, refreshMs]);

  return { data, error, loading, reload, updateData };
}
